import { isNull } from 'lodash/fp';
import { push } from 'redux-first-history';
import { all, call, fork, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { fetchOpinionAverageBreakdown, fetchOpinionJurisdictionCoverage, fetchOpinionJurisdictionSummary, fetchOpinionsAverage } from '../../../../services/analytics';
import { fetchOpinionsStarted, openOpinionStarted } from '../../my-opinions/store/actions';
import { fetchJurisdictionCoverageFailed, fetchJurisdictionCoverageStarted, fetchJurisdictionCoverageSuccessful, fetchOpinionAverageBreakdownFailed, fetchOpinionAverageBreakdownStarted, fetchOpinionAverageBreakdownSuccessful, fetchOpinionsAverageFailed, fetchOpinionsAverageStarted, fetchOpinionsAverageSuccessful, fetchSingleDataJurisdictionFailed, fetchSingleDataJurisdictionStarted, fetchSingleDataJurisdictionSuccessful, redirectToOpinion, setJurisdictionCounterpartyCoverage, setJurisdictionTransactionCoverage, setOpinionAnalyticsScope, setOpinionAverageView, setOpinionSingleField, setOpinionTileInView, setSelectedSingleFieldJurisdiction } from './actions';
import { ExtensiveOpinionAverage, OpinionAnalyticsActionTypes, OpinionAverage, OpinionAverageView, OpinionCoverageAnalytics, OpinionMapAnalytics, OpinionSingleField } from './types';
import { getOpinionAnalyticsScope, getOpinionSingleField, getSelectedCounterpartyCoverage, getSelectedTransactionCoverage, getViewSignOffAnswers } from './selectors';
import { getDefaultTransactionCoverageForScope, getDefaultSingleFieldForScope, DEFAULT_COUNTERPARTY_COVERAGE } from './utils';
import { OpinionScope } from '../../../admin/opinions/store/types';
import { MimeType } from '../../../constants/MimeType';

export function* attemptRedirectTileInView({ payload }: ReturnType<typeof setOpinionTileInView>) {
    const { tileInView, shouldRedirect } = payload;
    if (shouldRedirect) {
        const redirectUrl = isNull(tileInView) ? '/opinions/analytics' : `/opinions/analytics/${tileInView.replaceAll(' ', '-')}`;
        yield put(push(redirectUrl));
    }
}

function* setTileInViewWatcher() {
    yield takeLatest(OpinionAnalyticsActionTypes.SET_OPINION_ANALYTICS_TILE_IN_VIEW, attemptRedirectTileInView);
}

export function* attemptFetchOpinionJurisdictionSummary() {
    try {
        const { fieldId, sectionId }: OpinionSingleField = yield select(getOpinionSingleField);
        const viewSignOffAnswers: boolean = yield select(getViewSignOffAnswers);
        const scope: OpinionScope = yield select(getOpinionAnalyticsScope);
        const analytics: OpinionMapAnalytics[] = yield call(fetchOpinionJurisdictionSummary, { scope, sectionId, fieldId, viewSignOffAnswers });
        yield put(fetchSingleDataJurisdictionSuccessful(analytics));
    } catch (e) {
        yield put(fetchSingleDataJurisdictionFailed((e as Error).message));
    }
}

function* fetchOpinionJurisdictionSummaryWatcher() {
    yield takeLatest(OpinionAnalyticsActionTypes.FETCH_SINGLE_DATA_JURISDICTION_ANALYTICS_STARTED, attemptFetchOpinionJurisdictionSummary);
}

export function* attemptFetchOpinionsAverage() {
    try {
        const scope: OpinionScope = yield select(getOpinionAnalyticsScope);
        const { opinionsWithAverage }: { opinionsWithAverage: OpinionAverage[]; } = yield call(fetchOpinionsAverage, { scope });
        yield put(fetchOpinionsAverageSuccessful(opinionsWithAverage));
    } catch (e) {
        yield put(fetchOpinionsAverageFailed((e as Error).message));
    }
}

function* fetchOpinionsAverageWatcher() {
    yield takeLatest(OpinionAnalyticsActionTypes.FETCH_OPINIONS_AVERAGE_ANALYTICS_STARTED, attemptFetchOpinionsAverage);
}

export function* attemptFetchOpinionAverageBreakdown({ payload }: ReturnType<typeof fetchOpinionAverageBreakdownStarted>) {
    try {
        const opinionId = payload;
        const scope: OpinionScope = yield select(getOpinionAnalyticsScope);
        const extensiveOpinionAverage: ExtensiveOpinionAverage = yield call(fetchOpinionAverageBreakdown, { opinionId, scope });
        yield put(fetchOpinionAverageBreakdownSuccessful(extensiveOpinionAverage));
    } catch (e) {
        yield put(fetchOpinionAverageBreakdownFailed((e as Error).message));
    }
}

function* fetchOpinionAverageBreakdownWatcher() {
    yield takeLatest(OpinionAnalyticsActionTypes.FETCH_OPINION_AVERAGE_BREAKDOWN_STARTED, attemptFetchOpinionAverageBreakdown);
}

export function* attemptFetchOpinionJurisdiction() {
    yield put(fetchSingleDataJurisdictionStarted());
}

function* setOpinionSingleFieldWatcher() {
    yield takeLatest(OpinionAnalyticsActionTypes.SET_OPINION_SINGLE_FIELD, attemptFetchOpinionJurisdiction);
}

export function* attemptUpdateOpinionScope({ payload }: ReturnType<typeof setOpinionAnalyticsScope>) {
    const defaultSingleField = getDefaultSingleFieldForScope(payload);
    const transactionCoverage = getDefaultTransactionCoverageForScope(payload);
    const counterpartyCoverage = DEFAULT_COUNTERPARTY_COVERAGE;
    yield put(setSelectedSingleFieldJurisdiction(null));
    yield put(setOpinionSingleField(defaultSingleField));
    yield put(setJurisdictionCounterpartyCoverage(counterpartyCoverage));
    yield put(setJurisdictionTransactionCoverage(transactionCoverage));
    yield put(fetchOpinionsAverageStarted());
    yield put(fetchJurisdictionCoverageStarted());
    yield put(setOpinionAverageView(OpinionAverageView.OPINIONS));
}

function* setOpinionScopeWatcher() {
    yield takeLatest(OpinionAnalyticsActionTypes.SET_OPINION_ANALYTICS_SCOPE, attemptUpdateOpinionScope);
}

export function* attemptRedirectToOpinion({ payload }: ReturnType<typeof redirectToOpinion>) {
    const { opinionId, location } = payload;
    yield put(fetchOpinionsStarted(1));
    yield put(openOpinionStarted(location, MimeType.PDF, opinionId));
}

function* goToOpinion() {
    yield takeLeading(OpinionAnalyticsActionTypes.REDIRECT_TO_OPINION, attemptRedirectToOpinion);
}

export function* viewMySignOffToggleUpdated() {
    yield put(fetchSingleDataJurisdictionStarted());
}

function* mySignOffToggleWatcher() {
    yield takeLatest(OpinionAnalyticsActionTypes.SET_VIEW_SIGN_OFF_ANSWERS, viewMySignOffToggleUpdated);
}

export function* attemptFetchOpinionJurisdictionCoverage() {
    try {
        const scope: OpinionScope = yield select(getOpinionAnalyticsScope);
        const counterpartyCoverage: string | null = yield select(getSelectedCounterpartyCoverage);
        const transactionCoverage: string | null = yield select(getSelectedTransactionCoverage);
        const analytics: OpinionCoverageAnalytics[] = yield call(fetchOpinionJurisdictionCoverage, { scope, counterpartyCoverage, transactionCoverage });
        yield put(fetchJurisdictionCoverageSuccessful(analytics));
    } catch (e) {
        yield put(fetchJurisdictionCoverageFailed((e as Error).message));
    }
}

function* fetchOpinionJurisdictionCoverageWatcher() {
    yield takeLatest(OpinionAnalyticsActionTypes.FETCH_JURISDICTION_COVERAGE_ANALYTICS_STARTED, attemptFetchOpinionJurisdictionCoverage);
}

export function* attemptUpdateJurisdictionCoverageResults() {
    yield put(fetchJurisdictionCoverageStarted());
}

function* updateJurisdictionCoverageResultsWatcher() {
    yield takeLatest(OpinionAnalyticsActionTypes.SET_SELECTED_ANALYTICS_COVERAGE, attemptUpdateJurisdictionCoverageResults);
}

export function* opinionAnalyticsSaga() {
    yield all([
        fork(setTileInViewWatcher),
        fork(fetchOpinionJurisdictionSummaryWatcher),
        fork(setOpinionSingleFieldWatcher),
        fork(setOpinionScopeWatcher),
        fork(fetchOpinionsAverageWatcher),
        fork(goToOpinion),
        fork(fetchOpinionAverageBreakdownWatcher),
        fork(mySignOffToggleWatcher),
        fork(fetchOpinionJurisdictionCoverageWatcher),
        fork(updateJurisdictionCoverageResultsWatcher)
    ]);
}
